import { DialogConfirm } from '../../../components/elements/DialogConfirm';
import { Stack, IconButton } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createConditionReportSchema } from 'utils/validate';
import { InputText } from '../../../components/elements/InputText';
import React, { useEffect, useRef } from 'react';
import { useInjection } from '../../../components/modules/ModalNewLead/hook';
import { Autocomplete } from '@react-google-maps/api';
import CustomDatePicker from '../../../components/elements/InputDate';
import dayjs from 'dayjs';
import theme from '../../../../styles/theme';
import Typography from '@mui/material/Typography';
import { InputSelectImage } from '../../../components/elements/InputSelectImage';

type ModalReportProps = {
  isOpen: boolean;
  fileId: number;
  onClose: () => void;
  onSubmitSuccess: (value: CreateConditionReportDto) => void;
};

export interface CreateConditionReportDto {
  fileId: number;
  address: string;
  storageToDate: string;
  inspectionDate: string;
  dailyStorageRate: string;
  reasonFor?: string;
  signedVsa?: string;
  possibleRedemption?: string;
  kilometers?: number;
  keys?: string;
  glass?: string;
  tires?: string;
  wheels?: string;
  paint?: string;
  front?: string;
  drivSide?: string;
  passSide?: string;
  rear?: string;
  interior?: string;
  plates?: string;
  personalProperty?: string;
  comments?: string;
  files?: File[];
}

export const ModalReport = ({ isOpen, onClose, onSubmitSuccess, fileId }: ModalReportProps) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<CreateConditionReportDto>({
    resolver: yupResolver(createConditionReportSchema),
    defaultValues: {
      fileId
    }
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      fileId,
      address: '',
      inspectionDate: undefined,
      storageToDate: undefined,
      dailyStorageRate: '',
      reasonFor: '',
      signedVsa: '',
      possibleRedemption: '',
      kilometers: undefined,
      keys: '',
      glass: '',
      tires: '',
      wheels: '',
      paint: '',
      front: '',
      drivSide: '',
      passSide: '',
      rear: '',
      interior: '',
      plates: '',
      personalProperty: '',
      comments: '',
      files: []
    });
  }, [isOpen, fileId]);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;
    const newFiles = Array.from(fileList);
    setValue('files', [...(watch('files') || []), ...newFiles] as File[]);
  };

  const { onPlaceChanged, onLoadAutoComplete } = useInjection();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <DialogConfirm
      width={'600px'}
      textButton={'Save Report'}
      onSubmit={handleSubmit(onSubmitSuccess)}
      textTitle={'Create Report'}
      isOpen={isOpen}
      onClose={onClose}>
      <Stack width={'100%'} gap={2}>
        <Autocomplete onLoad={onLoadAutoComplete} onPlaceChanged={onPlaceChanged}>
          <InputText
            inputError={errors.address?.message}
            placeholder={'Address'}
            title={'Inspection Address'}
            inputProps={{
              ...register('address')
            }}
          />
        </Autocomplete>
        <Controller
          name={'inspectionDate'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CustomDatePicker
              inputError={errors.inspectionDate?.message}
              title={'Inspection Date'}
              value={value ? dayjs(value) : null}
              onChange={onChange}
              datePickerProps={{}}
            />
          )}
        />
        <InputText
          inputError={errors.storageToDate?.message}
          placeholder={'Storage to Date'}
          title={'Storage to Date'}
          inputProps={{ ...register('storageToDate') }}
        />
        <InputText
          inputError={errors.dailyStorageRate?.message}
          placeholder={'Daily Storage Rate'}
          title={'Daily Storage Rate'}
          inputProps={{
            ...register('dailyStorageRate')
          }}
        />
        <InputText
          inputError={errors.reasonFor?.message}
          placeholder={'Enter Reason'}
          title={'Reason for Impound'}
          inputProps={{ ...register('reasonFor') }}
        />

        <InputText
          inputError={errors.signedVsa?.message}
          placeholder={'Signed VSA'}
          title={'Signed VSA'}
          inputProps={{ ...register('signedVsa') }}
        />

        <InputText
          inputError={errors.possibleRedemption?.message}
          placeholder={'Possible Redemption'}
          title={'Possible Redemption'}
          inputProps={{ ...register('possibleRedemption') }}
        />

        <InputText
          inputError={errors.kilometers?.message}
          placeholder={'Kilometers'}
          title={'Kilometers'}
          inputProps={{ ...register('kilometers'), type: 'number' }}
        />

        <InputText
          inputError={errors.keys?.message}
          placeholder={'Keys'}
          title={'Keys'}
          inputProps={{ ...register('keys') }}
        />

        <InputText
          inputError={errors.glass?.message}
          placeholder={'Glass'}
          title={'Glass'}
          inputProps={{ ...register('glass') }}
        />

        <InputText
          inputError={errors.tires?.message}
          placeholder={'Tires'}
          title={'Tires'}
          inputProps={{ ...register('tires') }}
        />

        <InputText
          inputError={errors.wheels?.message}
          placeholder={'Wheels'}
          title={'Wheels'}
          inputProps={{ ...register('wheels') }}
        />

        <InputText
          inputError={errors.paint?.message}
          placeholder={'Paint'}
          title={'Paint'}
          inputProps={{ ...register('paint') }}
        />

        <InputText
          inputError={errors.front?.message}
          placeholder={'Front'}
          title={'Front'}
          inputProps={{ ...register('front') }}
        />

        <InputText
          inputError={errors.drivSide?.message}
          placeholder={'Driver Side'}
          title={'Driver Side'}
          inputProps={{ ...register('drivSide') }}
        />

        <InputText
          inputError={errors.passSide?.message}
          placeholder={'Passenger Side'}
          title={'Passenger Side'}
          inputProps={{ ...register('passSide') }}
        />
        <InputText
          inputError={errors.rear?.message}
          placeholder={'Rear'}
          title={'Rear'}
          inputProps={{ ...register('rear') }}
        />

        <InputText
          inputError={errors.interior?.message}
          placeholder={'Interior'}
          title={'Interior'}
          inputProps={{ ...register('interior') }}
        />

        <InputText
          inputError={errors.plates?.message}
          placeholder={'Plates'}
          title={'Plates'}
          inputProps={{ ...register('plates') }}
        />

        <InputText
          inputError={errors.personalProperty?.message}
          placeholder={'Personal Property'}
          title={'Personal Property'}
          inputProps={{ ...register('personalProperty') }}
        />

        <InputText
          inputError={errors.comments?.message}
          placeholder={'Comments'}
          title={'Comments'}
          inputProps={{ ...register('comments') }}
        />
        <Stack>
          <InputSelectImage
            listFileName={watch('files')?.map((item) => item.name)}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <IconButton
            style={{
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'start',
              width: 'fit-content',
              borderRadius: '20px'
            }}
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}>
            <Typography fontSize={'13px'} fontWeight={500} mb={1} color={theme.colors.dark1}>
              Upload Photos
            </Typography>
            <img
              src={'/icons/icon_upload.svg'}
              style={{ width: '30px', height: '30px' }}
              alt={'icon'}
            />
          </IconButton>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
          {watch('files') &&
            Array.from(watch('files') as File[]).map((file, idx) => (
              <Stack key={idx} position="relative" sx={{ width: 120, height: 120 }}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const newFiles = (watch('files') || []).filter((_, i) => i !== idx);
                    setValue('files', newFiles);
                  }}
                  sx={{ position: 'absolute', top: -10, right: -10, background: '#fff' }}>
                  <HighlightOffIcon color={'error'} fontSize="small" />
                </IconButton>
              </Stack>
            ))}
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
